import { useEffect, useMemo } from 'react';
import { useGetCloudAccountsQuery } from '../../../api';
import { processCloudAccounts } from '../utils';
import { isApplianceReady } from '../../../lib/helpers';
import { isVmLevelMigrationEnabled } from '../../../pages/Clouds/utils';

/**
 * Custom hook for cloud account processing and auto-selection
 */
export const useCloudAccountProcessing = ({
    selectedSources,
    migrationTypeFlags,
    selectedAccountId,
    handleTargetAccountChange,
    form,
    title,
}) => {
    const userOrg = sessionStorage.getItem('userOrg');

    const isInSitu = title && title.toLowerCase().includes('modernization');
    const isMigrateByVM = isInSitu ? false : migrationTypeFlags.isMigrateByVM;

    // Get appliance_id for VM-based migrations
    const vmApplianceId = useMemo(() => {
        if (!isMigrateByVM) {
            return null;
        }
        const vmSources = selectedSources.filter((source) => source.collection_type === 'vm');

        if (vmSources.length === 0) {
            return null;
        }

        // Check if all VM sources have the same appliance_id
        const firstApplianceId = vmSources[0]?.appliance_id;
        const allSameAppliance = vmSources.every((source) => source.appliance_id === firstApplianceId);

        return allSameAppliance ? firstApplianceId : null;
    }, [isMigrateByVM, selectedSources]);

    // For OS-based sources: Get previously used appliance_id for inspection
    const previouslyUsedApplianceId = useMemo(() => {
        if (isMigrateByVM || selectedSources.length === 0) {
            return null;
        }

        const inspectedSources = selectedSources.filter((source) => source.appliance_id);

        if (inspectedSources.length === 0) {
            return null;
        }

        const firstApplianceId = inspectedSources[0].appliance_id;
        const allSameAppliance = inspectedSources.every((source) => source.appliance_id === firstApplianceId);

        return allSameAppliance ? firstApplianceId : inspectedSources[0].appliance_id;
    }, [isMigrateByVM, selectedSources]);

    // Fetch cloud accounts
    const {
        data: cloudAccountsData,
        isLoading: isLoadingAccounts,
        error: accountsError,
    } = useGetCloudAccountsQuery({
        userOrg,
        size: 100,
        sort: 'name,asc',
    });

    const processedCloudAccounts = useMemo(() => {
        // First process accounts with appliance readiness checks
        let accounts = processCloudAccounts(cloudAccountsData, isMigrateByVM);

        if (isInSitu) {
            accounts = accounts.filter((cloudAccount) => cloudAccount.cloud_type === 'rivermeadow_standalone');
        }

        // For VM-based migrations, disable non-VM-enabled MAs
        if (isMigrateByVM) {
            accounts = accounts.map((account) => {
                /**
                 * Workaround:
                 * The isVmLevelMigrationEnabled helper only checks `appliance?.vm_level_migration_enabled`.
                 * vm_level_migration_enabled is a FE-level flag that exists only for Public CA.
                 * This flag does not exist on ME and is set when enabling VM-based migration
                 *
                 * Therefore, we use `isApplianceReady` check also, which determines whether an appliance is VM-based
                 * by checking `source_workers.length` and validating their `heartbeat_status`.
                 */
                const isVmEnabled =
                    isVmLevelMigrationEnabled(account) || isApplianceReady(account.appliance, isMigrateByVM);
                return {
                    ...account,
                    disabled: account.disabled || !isVmEnabled,
                    isVmEnabled,
                };
            });
        }

        return accounts;
    }, [cloudAccountsData, isMigrateByVM, isInSitu]);

    const nonDisabledAccounts = useMemo(
        () => processedCloudAccounts.filter((acc) => !acc.disabled),
        [processedCloudAccounts],
    );
    const shouldPreselect = nonDisabledAccounts.length === 1;

    useEffect(() => {
        if (selectedAccountId) {
            return;
        }

        let accountToSelect = null;

        if (isMigrateByVM) {
            if (vmApplianceId) {
                accountToSelect = processedCloudAccounts.find(
                    (acc) => acc.appliance_id === vmApplianceId && !acc.disabled,
                );
            }

            if (!accountToSelect) {
                accountToSelect = processedCloudAccounts.find((acc) => acc.isVmEnabled && !acc.disabled);
            }
        } else {
            if (previouslyUsedApplianceId) {
                accountToSelect = processedCloudAccounts.find(
                    (acc) => acc.appliance_id === previouslyUsedApplianceId && !acc.disabled,
                );
            }

            if (!accountToSelect && nonDisabledAccounts.length > 0) {
                accountToSelect = nonDisabledAccounts[0];
            }
        }

        if (accountToSelect) {
            handleTargetAccountChange(accountToSelect.id, processedCloudAccounts);
            form.setFieldsValue({ targetAccount: accountToSelect.id });
        }
    }, [
        selectedAccountId,
        isMigrateByVM,
        vmApplianceId,
        previouslyUsedApplianceId,
        processedCloudAccounts,
        shouldPreselect,
        nonDisabledAccounts,
        handleTargetAccountChange,
        form,
    ]);

    return {
        // API state
        cloudAccountsData,
        isLoadingAccounts,
        accountsError,

        // Processed data
        processedCloudAccounts,
        nonDisabledAccounts,
        shouldPreselect,

        // Migration context flags
        isInSitu,
        isMigrateByVM,
        vmApplianceId,
        previouslyUsedApplianceId,
    };
};
